import { formatText } from '../components/common/TextFormatterProvider';

export const CATEGORY_TYPE_OPTIONS = ['Text', 'Number', 'Date', 'Yes/No'];

// The platform's one reading of a boolean on screen: the type is called Yes/No,
// so a value reads Yes or No everywhere — never a blank cell (React renders a
// boolean child as nothing) and never "true"/"false".
export const formatBooleanValue = (value) => (value ? 'Yes' : 'No');

// One reading of a scalar for display where it is not a typed table cell —
// attribute chips, metadata rows, a lineage's current value. A boolean is
// Yes/No, an object its JSON, anything else its text with underscores read as
// spaces. Numbers and dates are left exactly as stored: these surfaces carry
// identifiers and years, which the tables' amount formatting would misread.
export function formatDisplayValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return formatBooleanValue(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return formatText(String(value));
}

// The aliases the backend's normalize_declared_type accepts. A table document can hold
// any of them — update_types_recursive rewrites a schema's types to their BigQuery
// names on some read paths — and the two sides must agree on what a column IS, or the
// editor offers conditions the runtime rejects and refuses ones it would have accepted.
const NUMBER_TYPE_ALIASES = new Set(['number', 'float', 'float64', 'integer', 'int', 'numeric']);
const DATE_TYPE_ALIASES = new Set(['date', 'datetime', 'timestamp']);
const BOOLEAN_TYPE_ALIASES = new Set(['yes/no', 'boolean', 'bool']);
// 'text' / 'string' / 'str' need no set: they are the fallthrough.

export function normalizeCategoryType(type) {
  if (!type) return 'Text';

  const normalized = String(type).trim().toLowerCase();
  if (NUMBER_TYPE_ALIASES.has(normalized)) return 'Number';
  if (DATE_TYPE_ALIASES.has(normalized)) return 'Date';
  if (BOOLEAN_TYPE_ALIASES.has(normalized)) return 'Yes/No';
  return 'Text';
}

export function isEmptyTypedValue(value) {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

export function canonicalizeTagValue(value, type) {
  const declaredType = normalizeCategoryType(type);

  if (isEmptyTypedValue(value)) {
    return null;
  }

  if (declaredType === 'Number') {
    const parsed = Number(String(value).replace(/,/g, '').trim());
    if (!Number.isFinite(parsed)) {
      throw new Error('Enter a valid number');
    }
    return parsed;
  }

  if (declaredType === 'Yes/No') {
    if (typeof value === 'boolean') return value;
    const normalized = String(value).trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n'].includes(normalized)) return false;
    throw new Error('Select a valid Yes/No value');
  }

  if (declaredType === 'Date') {
    const normalized = String(value).trim();
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);
    if (!match) {
      throw new Error('Enter a valid date');
    }
    // The shape alone admits 2026-02-31, which no calendar has: the backend
    // refuses or nulls such a value long after it was stored (M209).
    const [year, month, day] = match.slice(1).map(Number);
    const date = new Date(0);
    date.setUTCFullYear(year, month - 1, day);
    if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
      throw new Error('Enter a valid date');
    }
    return normalized;
  }

  return String(value);
}

export function buildSearchText(value, name = null) {
  const basis = name ?? value;
  if (basis === null || basis === undefined) return '';
  return String(basis).toLowerCase();
}

// A value's Firestore document id is its label with spaces turned into
// underscores. The label therefore cannot contain a path separator, an
// underscore (it would collide with the space form of another value — "A B" and
// "A_B" would share one document), or be one of Firestore's reserved ids.
// Returns the message to show, or null when the label is usable.
// Words no category, value or profile may be called: the sidebar's own
// "None" (no grouping) and "Unassigned" (no value), and anything JavaScript
// puts on every object ("constructor", "toString", "__proto__" …), which a
// plain object keyed by user text would resolve to a function.
export function reservedNameError(label, noun = 'Value') {
  const trimmed = String(label ?? '').trim();
  const lower = trimmed.toLowerCase();
  if (lower === 'none' || lower === 'unassigned' || (trimmed !== '' && trimmed in Object.prototype)) {
    return `${noun} cannot be "${trimmed}" — that word is reserved`;
  }
  return null;
}

export function tagValueIdError(label) {
  const trimmed = String(label ?? '').trim();
  if (trimmed === '') return 'Value must contain alphanumeric characters';
  const reserved = reservedNameError(trimmed);
  if (reserved) return reserved;
  if (trimmed.includes('/')) return 'Value cannot contain "/"';
  if (trimmed.includes('_')) return 'Value cannot contain "_" — use a space instead';
  if (trimmed === '.' || trimmed === '..') return `Value cannot be "${trimmed}"`;
  if (new TextEncoder().encode(trimmed).length > 1500) return 'Value is too long';
  return null;
}

export function getTypedValueLabel(value, type, fallbackName = null) {
  if (fallbackName) return String(fallbackName);
  if (value === null || value === undefined || value === '') return '';
  if (normalizeCategoryType(type) === 'Yes/No') return value ? 'Yes' : 'No';
  return String(value);
}

export function toSelectionOption(docData = {}, fallbackId = '') {
  const type = normalizeCategoryType(docData.type);
  const canonicalValue = Object.prototype.hasOwnProperty.call(docData, 'value')
    ? docData.value
    : (docData.name ?? fallbackId);

  return {
    id: fallbackId || String(canonicalValue ?? docData.name ?? ''),
    type,
    name: String(docData.name ?? getTypedValueLabel(canonicalValue, type)),
    value: canonicalValue,
    search_text: docData.search_text ?? buildSearchText(canonicalValue, docData.name),
  };
}
